import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { AuditEvent } from "../../api/types";
import { S } from "../../styles";
import { LoadingState } from "../../components/QueryStates";

const ACTION_COLOR: Record<string, string> = {
  CREATE: "#2f9e44",
  UPDATE: "#1971c2",
  DELETE: "#c92a2a",
  LOGIN: "#7c3aed",
  LOGOUT: "#868e96",
  EXPORT: "#e67700",
  SHIPPING_NOTICE: "#0c8599",
  RECEIPT_CONFIRMATION: "#2f9e44",
  // The event that closes demand, and the one that gives up a verdict. Both
  // are the reason someone opens this panel on a requirement or a shipment, so
  // neither should arrive as the grey default.
  FULFILLED_BY_RECEIPT: "#2f9e44",
  RECEIVING_INSPECTION: "#7c3aed",
  RECEIVING_INSPECTION_REOPENED: "#c92a2a",
};

export function actionStyle(action: string): React.CSSProperties {
  const c = ACTION_COLOR[action.toUpperCase()] ?? "#495057";
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase" as const,
    background: c + "1a",
    color: c,
    border: `1px solid ${c}44`,
    whiteSpace: "nowrap" as const,
  };
}

function mono(id: string, len = 8): string {
  return id.length > len ? `${id.slice(0, len)}…` : id;
}

/**
 * The address column, which has three states rather than two.
 *
 * Collapsing them all to one dash is what made this column look broken. An
 * event with no HTTP request behind it — a supplier webhook, the email poller,
 * a Temporal activity — never had an address to record, and never will; that
 * is the column working correctly, and the Actor beside it already reads
 * "system". A person's action with no address is a different fact: there was a
 * caller, and we failed to capture where they were. Saying so is more useful
 * than a dash, and much more useful than backfilling a plausible-looking
 * address that nobody actually came from.
 */
function AddressCell({ e }: { e: AuditEvent }) {
  if (e.ip_address) return <>{e.ip_address}</>;

  if (!e.user_id)
    return (
      <span
        title="No HTTP request behind this event — it was raised by a supplier webhook, the email poller or a workflow activity, so there is no caller address to record."
        style={{ color: "#b8b8b8" }}
      >
        —
      </span>
    );

  return (
    <span
      title="This action had a caller, but their address was not recorded. Events from before the proxy-header fix captured the reverse proxy's address instead of the browser's, and some paths captured none at all."
      style={{ color: "#b0b0b0", fontStyle: "italic" }}
    >
      not captured
    </span>
  );
}

function AuditEventRow({
  e,
  showResource,
}: {
  e: AuditEvent;
  showResource: boolean;
}) {
  const [open, setOpen] = useState(false);
  const hasDetails =
    Boolean(e.changes && Object.keys(e.changes).length > 0) ||
    !!e.ip_address ||
    !!e.user_agent;

  return (
    <>
      <tr
        style={{
          cursor: hasDetails ? "pointer" : "default",
          background: open ? "#f8f9ff" : undefined,
          borderBottom: "1px solid #f0f0f0",
        }}
        onClick={() => hasDetails && setOpen((x) => !x)}
      >
        <td style={{ padding: "10px 12px", width: 24, color: "#aaa" }}>
          {hasDetails ? (open ? "▾" : "▸") : ""}
        </td>
        <td style={{ padding: "10px 12px", fontSize: 12, color: "#888" }}>
          {new Date(e.created_at).toLocaleString()}
        </td>
        <td style={{ padding: "10px 12px" }}>
          <span style={actionStyle(e.action)}>{e.action}</span>
        </td>
        {showResource && (
          <td style={{ padding: "10px 12px", fontSize: 12, color: "#555" }}>
            {e.resource_type}
            {e.resource_id && (
              <span style={{ fontFamily: "monospace", color: "#888" }}>
                {" "}
                {mono(e.resource_id)}
              </span>
            )}
          </td>
        )}
        <td style={{ padding: "10px 12px", fontSize: 12, color: "#555" }}>
          {e.actor_email ? (
            <span title={e.user_id ?? undefined}>{e.actor_email}</span>
          ) : e.user_id ? (
            <span style={{ fontFamily: "monospace" }} title={e.user_id}>
              {mono(e.user_id)}
            </span>
          ) : (
            <span style={S.muted}>system</span>
          )}
        </td>
        <td style={{ padding: "10px 12px", fontSize: 12, color: "#888" }}>
          <AddressCell e={e} />
        </td>
      </tr>
      {open && hasDetails && (
        <tr style={{ background: "#f8f9ff" }}>
          <td
            colSpan={showResource ? 6 : 5}
            style={{ padding: "0 24px 16px 48px" }}
          >
            {e.changes && Object.keys(e.changes).length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase" as const,
                    color: "#888",
                    marginBottom: 4,
                    letterSpacing: 0.5,
                  }}
                >
                  Changes
                </div>
                <pre
                  style={{
                    margin: 0,
                    fontSize: 11,
                    background: "#1a1a2e",
                    color: "#a8d8a8",
                    borderRadius: 6,
                    padding: "8px 12px",
                    overflow: "auto",
                    maxHeight: 260,
                  }}
                >
                  {JSON.stringify(e.changes, null, 2)}
                </pre>
              </div>
            )}
            <div
              style={{
                display: "flex",
                gap: 20,
                marginTop: 10,
                fontSize: 12,
                color: "#888",
                flexWrap: "wrap" as const,
              }}
            >
              <span>
                Event ID:{" "}
                <span style={{ fontFamily: "monospace" }}>
                  {mono(e.id, 16)}
                </span>
              </span>
              {e.user_agent && (
                <span title={e.user_agent}>
                  UA:{" "}
                  <span style={{ fontFamily: "monospace" }}>
                    {e.user_agent.length > 60
                      ? `${e.user_agent.slice(0, 60)}…`
                      : e.user_agent}
                  </span>
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function AuditEventTable({
  events,
  showResource = true,
}: {
  events: AuditEvent[];
  showResource?: boolean;
}) {
  const headers = showResource
    ? ["When", "Action", "Resource", "Actor", "IP"]
    : ["When", "Action", "Actor", "IP"];
  return (
    <div
      style={{
        overflowX: "auto",
        border: "1px solid #e8eaf0",
        borderRadius: 10,
        background: "#fff",
      }}
    >
      <table
        style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
      >
        <thead>
          <tr
            style={{ background: "#f4f5f9", borderBottom: "2px solid #e8eaf0" }}
          >
            <th style={{ width: 24, padding: "8px 12px" }} />
            {headers.map((h) => (
              <th
                key={h}
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  fontWeight: 600,
                  color: "#555",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <AuditEventRow key={e.id} e={e} showResource={showResource} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Embeddable activity trail for a single record (RFQ, quote, PO, …).
 * Lazily fetches when expanded. Render only for roles allowed to read audit
 * events (finance / manager / admin) — the API returns 403 otherwise.
 */
export function AuditTrail({
  resourceType,
  resourceId,
  label = "Activity History",
}: {
  resourceType: string;
  resourceId: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["audit-events", resourceType, resourceId],
    queryFn: () => api.listAuditEvents({ resourceType, resourceId, limit: 50 }),
    enabled: open,
  });

  const events = data?.items ?? [];

  return (
    <div style={{ marginTop: 12 }}>
      <button
        style={{ ...S.btnSm, background: "#495057" }}
        onClick={() => setOpen((x) => !x)}
      >
        🕓 {open ? "Hide" : "Show"} {label}
        {data ? ` (${data.total})` : ""}
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          {isLoading && <LoadingState label="Loading activity…" />}
          {error && <p style={S.err}>⚠ {error.message}</p>}
          {!isLoading && !error && events.length === 0 && (
            <p style={S.muted}>No recorded activity for this record.</p>
          )}
          {events.length > 0 && (
            <>
              <AuditEventTable events={events} showResource={false} />
              <div style={{ marginTop: 6 }}>
                <button style={S.btnSm} onClick={() => void refetch()}>
                  ↺ Refresh
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
